import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import confetti from "canvas-confetti";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { BRANCH_META } from "@/lib/branches";

export default function PaymentSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState("polling");
    const [attempts, setAttempts] = useState(0);
    const { refresh } = useAuth();
    const nav = useNavigate();

    useEffect(() => {
        if (!sessionId) {
            setStatus("error");
            return;
        }
        let stopped = false;
        const poll = async () => {
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    if (!stopped) {
                        setStatus("paid");
                        confetti({
                            particleCount: 120,
                            spread: 90,
                            origin: { y: 0.4 },
                            colors: [BRANCH_META.arts.color, BRANCH_META.humanities.color, "#FDFBF7"],
                        });
                        await refresh();
                    }
                    return;
                }
                if (data.status === "expired" || attempts > 20) {
                    if (!stopped) setStatus("timeout");
                    return;
                }
                if (!stopped) {
                    setAttempts((a) => a + 1);
                    setTimeout(poll, 1500);
                }
            } catch {
                if (!stopped) setStatus("error");
            }
        };
        poll();
        return () => {
            stopped = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId]);

    return (
        <div className="min-h-screen w-full cosmic-sky flex items-center justify-center px-6">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="crystal rounded-3xl p-8 max-w-md w-full text-center"
            >
                {status === "polling" && (
                    <>
                        <div className="w-16 h-16 rounded-full mx-auto animate-pulse-node bg-slate-900" />
                        <h1 className="font-display font-black text-3xl tracking-tighter text-slate-900 mt-4">
                            Confirming your Beacon…
                        </h1>
                        <p className="text-slate-600 text-sm mt-2">
                            One moment — Stripe is finalising the payment.
                        </p>
                    </>
                )}
                {status === "paid" && (
                    <>
                        <div className="flex justify-center">
                            <CheckCircle2 className="w-16 h-16" color={BRANCH_META.trades.color} />
                        </div>
                        <h1
                            className="font-display font-black text-4xl tracking-tighter text-slate-900 mt-4"
                            data-testid="payment-success-title"
                        >
                            You're in.
                        </h1>
                        <p className="text-slate-600 mt-2">
                            Atlas Access is now active on your ledger. Every beacon, every workshop, every quest — open.
                        </p>
                        <button
                            onClick={() => nav("/atlas")}
                            className="arcade-btn w-full mt-6"
                            data-variant="primary"
                            data-testid="payment-success-continue"
                        >
                            Return to the map <ArrowRight className="w-4 h-4" />
                        </button>
                        <div className="mt-4">
                            <Link
                                to="/about"
                                className="text-slate-500 hover:text-slate-900 text-sm font-display font-bold underline underline-offset-4"
                                data-testid="post-payment-learn-more"
                            >
                                Learn more about Atlas →
                            </Link>
                        </div>
                    </>
                )}
                {(status === "timeout" || status === "error") && (
                    <>
                        <h1 className="font-display font-black text-3xl tracking-tighter text-slate-900">
                            Something didn't sync.
                        </h1>
                        <p className="text-slate-600 mt-2 text-sm">
                            If you were charged, refresh in a moment — Stripe webhooks can take up to a minute.
                        </p>
                        <Link to="/paywall" className="arcade-btn w-full mt-6" data-variant="ghost">
                            Try again
                        </Link>
                    </>
                )}
            </motion.div>
        </div>
    );
}
